import { useCallback, useEffect, useRef } from "react";

// A scanner is just a keyboard typing fast — while the user is focused in a text field
// (a search box, a quantity/date cell, anything editable) that's unrelated to scanning,
// that fast input IS the field value they're after, not a request to also trigger this
// hook's onComplete side effect (e.g. the global cart popping a product in while the user
// is typing in some other page's search box). Some callers instead build their whole
// workflow around scanning into/near a focused field (e.g. the inventory count screen,
// where the "quantité réelle" cell is auto-focused right after each scan so the next one
// can fire immediately) — those pass ignoreEditableFocus={false} to opt out.
const isEditableElement = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
};

/**
 * BarcodeScanner - Hook pour détecter l'entrée d'un scanner de code-barres
 * @param {Object} config Configuration du hook
 */
const BarcodeScanner = ({
  timeToEvaluate = 100,
  averageWaitTime = 50,
  startCharacter = [],
  endCharacter = [13, 27],
  onComplete,
  onError,
  minLength = 1,
  ignoreIfFocusOn,
  ignoreEditableFocus = true,
  stopPropagation = false,
  preventDefault = false,
  container = document,
}) => {
  const buffer = useRef([]);
  const timeout = useRef(null);

  const clearBuffer = () => {
    buffer.current = [];
  };

  const evaluateBuffer = () => {
    clearTimeout(timeout.current);

    const sum = buffer.current
      .map(({ time }, k, arr) => (k > 0 ? time - arr[k - 1].time : 0))
      .slice(1)
      .reduce((total, delta) => total + delta, 0);

    const avg = sum / (buffer.current.length - 1 || 1);

    const code = buffer.current
      .slice(startCharacter.length > 0 ? 1 : 0)
      .map(({ char }) => char)
      .join("");

    if (
      avg <= averageWaitTime &&
      buffer.current.slice(startCharacter.length > 0 ? 1 : 0).length >= minLength
    ) {
      onComplete(code);
    } else if (avg <= averageWaitTime && typeof onError === "function") {
      onError(code);
    }

    clearBuffer();
  };

  const onKeyDown = useCallback(
    (event) => {
      if (ignoreEditableFocus && (isEditableElement(event.target) || isEditableElement(document.activeElement))) {
        // Drop whatever was mid-buffer from before focus landed here, so its pending
        // timeout can't fire evaluateBuffer() on stale keystrokes once focus moves away.
        clearTimeout(timeout.current);
        clearBuffer();
        return;
      }

      if (event.target !== ignoreIfFocusOn) {
        if (endCharacter.includes(event.keyCode)) {
          evaluateBuffer();
        }
        if (
          buffer.current.length > 0 ||
          startCharacter.includes(event.keyCode) ||
          startCharacter.length === 0
        ) {
          clearTimeout(timeout.current);
          timeout.current = setTimeout(evaluateBuffer, timeToEvaluate);
          if (event.key.length === 1) {
            buffer.current.push({ time: performance.now(), char: event.key });
          }
        }
      }

      if (stopPropagation) event.stopPropagation();
      if (preventDefault) event.preventDefault();
    },
    [
      startCharacter,
      endCharacter,
      timeToEvaluate,
      onComplete,
      onError,
      minLength,
      ignoreIfFocusOn,
      ignoreEditableFocus,
      stopPropagation,
      preventDefault,
    ]
  );

  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);

  useEffect(() => {
    container.addEventListener("keydown", onKeyDown);
    return () => container.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);
};


/*const useElectronBarcodeScanner = (onScan) => {
  useEffect(() => {
    if (window.electronAPI?.onBarcodeScanned) {
      window.electronAPI.onBarcodeScanned((code) => {
        onScan(code);
      });
    }
  }, [onScan]);
};*/
/*const useElectronBarcodeScanner = (onScan) => {
  useEffect(() => {
    const listener = (_event, code) => {
      onScan(code);
    };

    if (window.electronAPI?.onBarcodeScanned) {
      window.electronAPI.onBarcodeScanned(listener);
    }

    return () => {
      if (window.electronAPI?.removeBarcodeListener) {
        window.electronAPI.removeBarcodeListener(listener);
      }
    };
  }, [onScan]);
};*/

export default BarcodeScanner;
//export {useElectronBarcodeScanner};